import { useMemo, useState } from 'react';
import { FieldArray, Form, Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import _ from 'lodash';
import styled from 'styled-components';
import { gql, useMutation } from '@apollo/client';
import { useParams } from 'react-router';
import { Button } from 'reactstrap';
import {
  InlineValidatedInput,
  SubmitButton,
} from './templates/template-components';
import FormContainer from './FormContainer';
import { ShrinkContent, ShrinkContentColumn } from './ShrinkContent';
import { useSetBreadcrumbEffect } from '../contexts/BreadcrumbContext';
import { useQueryWithFarmIdAndMsg } from '../utils/hooks';
import { transformNaNTo } from '../utils/yup';
import UnsavedFormPrompt from './basics/UnsavedFormPrompt';
import { CATEGORIES } from '../constants/operations';
import { asCurrencyWithCents, asQuantityWithUnit } from '../utils/formatters';
import { captureEvent } from '../utils/analytics';
import EditCropLink from './EditCropLink';
import IconButton from './basics/IconButton';
import EditCapexLink from './EditCapexLink';
import { ADJUSTMENT_TYPES } from '../constants/costAdjustments';
import {
  BlockIconSpan,
  CONTAINER_PADDING_X,
  RoundedGreyContainer,
} from './form-components/RoundedGreyContainer';
import ExpandableItemsList from './ExpandableItemsList';
import StandardTypeahead from './basics/StandardTypeahead';
import useMutateWithState from '../hooks/useMutateWithState';

const adjustmentsQuery = (adjustmentType, pluralAdjustmentType) => gql`
  query Farm${_.capitalize(
    pluralAdjustmentType,
  )}Query($farmId: ID!, $applyBuffer: Boolean!, $applyMargin: Boolean!) {
    farm(farmId: $farmId) {
      id
      category${_.capitalize(pluralAdjustmentType)} {
        id
        capexPercent
        geneticsPercent
        installationPercent
        managementPercent
        harvestPercent
        marketingPercent
        updatedAt
      }
    }
    rawCrops(farmId: $farmId) {
      id
      name
      updatedAt
      ${adjustmentType}Percent
      unitCost(applyBuffer: $applyBuffer, applyMargin: $applyMargin)
      operations {
        id
        name
        category
        assignment
      }
    }
    rawCapexes(farmId: $farmId) {
      id
      name
      updatedAt
      assignment
      ${adjustmentType}Percent
      unitCost(applyBuffer: $applyBuffer, applyMargin: $applyMargin)
    }
  }
`;

const getUpsertAdjustmentMutation = (pluralAdjustmentType) => {
  const pluralAdjustmentTypeCapitalized = _.capitalize(pluralAdjustmentType);
  return gql`
    mutation UpsertFarm${pluralAdjustmentTypeCapitalized}($input: UpsertFarm${pluralAdjustmentTypeCapitalized}Input!) {
      upsertFarm${pluralAdjustmentTypeCapitalized}(input: $input) {
        errors
        result {
          id
        }
      }
    }
  `;
};

// Try to parse a number, but default to returning zero if it is NaN
const asNumber = (n) => parseFloat(n) || 0;

const createValidationSchema = (percentValidator) => {
  const overridePercentValidator = percentValidator
    .nullable(true)
    .transform(transformNaNTo(null));

  const overridesValidator = yup.array().of(
    yup.object().shape({
      bufferPercent: overridePercentValidator,
      marginPercent: overridePercentValidator,
      subsidyPercent: overridePercentValidator,
    }),
  );

  const categoryPercentValidator = percentValidator
    .transform(transformNaNTo(0))
    .default(0);

  return yup.object().shape({
    geneticsPercent: categoryPercentValidator,
    installationPercent: categoryPercentValidator,
    managementPercent: categoryPercentValidator,
    harvestPercent: categoryPercentValidator,
    marketingPercent: categoryPercentValidator,
    capexPercent: categoryPercentValidator,
    geneticsPercentOverrides: overridesValidator,
    capexPercentOverrides: overridesValidator,
  });
};

const FormSectionContainer = styled(FormContainer)`
  overflow: visible;
`;

const SectionHeader = styled.div`
  display: flex;
  padding-right: ${CONTAINER_PADDING_X};
  h3 {
    flex-grow: 1;
  }
`;

const CostHeading = styled.div`
  width: calc((100% - ${CONTAINER_PADDING_X}) / 4);
  flex-shrink: 0;
`;

const TopSection = styled.div`
  border-bottom: 1px solid var(--border-color);
  h1 {
    margin: var(--header-margin-y) 0;
  }
`;

const StyledRoundedGreyContainer = styled(RoundedGreyContainer)`
  .override-label {
    flex-grow: 1;
    justify-content: flex-start;
  }
  .form-inline {
    flex-flow: nowrap;
  }
`;

const OverridesSection = ({
  name,
  adjustmentType,
  overrideOptions,
  overrideCategory,
  formatItem,
}) => {
  const { percentToMultiplier } = ADJUSTMENT_TYPES[adjustmentType];
  const [addingOverride, setAddingOverride] = useState(false);
  const { values, errors } = useFormikContext();
  const overrides = values[`${name}Overrides`];
  const availableOptions = overrideOptions?.filter(
    ({ id }) => !overrides.find(({ id: overrideId }) => id === overrideId),
  );
  const defaultValue = _.get(values, name) || 0;
  return (
    <FieldArray name={`${name}Overrides`}>
      {({ push, remove }) => (
        <>
          {overrides.map((override, i) => {
            const fieldName = `${name}Overrides[${i}].${adjustmentType}Percent`;
            const overrideValue = _.get(values, fieldName) ?? defaultValue;
            const error = _.get(errors, fieldName);
            return (
              <StyledRoundedGreyContainer key={override.id} className="mt-2">
                <Button
                  color="link"
                  onClick={() => {
                    captureEvent('cost-adjustment-override-removed', {
                      overrideCategory,
                      adjustmentType,
                    });
                    remove(i);
                  }}
                >
                  ×
                </Button>
                <InlineValidatedInput
                  formGroupClassName="form-inline m-0 flex-grow-1"
                  labelClassName="override-label"
                  maxWidth="70px"
                  label={formatItem(override)}
                  type="number"
                  step="any"
                  min={0}
                  name={fieldName}
                  unit="%"
                  parse={asNumber}
                />
                <span className="w-25 pr-0 text-center flex-shrink-0">
                  {asCurrencyWithCents(override.unitCost)}
                </span>
                <span className="w-25 pr-0 text-center flex-shrink-0">
                  {error
                    ? '-'
                    : asCurrencyWithCents(
                        override.unitCost * percentToMultiplier(overrideValue),
                      )}
                </span>
              </StyledRoundedGreyContainer>
            );
          })}
          {availableOptions.length > 0 &&
            (addingOverride ? (
              <StyledRoundedGreyContainer className="mt-2">
                <div className="d-flex align-items-center w-50">
                  <Button color="link" onClick={() => setAddingOverride(false)}>
                    ×
                  </Button>
                  <StandardTypeahead
                    id="override-typeahead"
                    size="sm"
                    labelKey="name"
                    minWidth="200px"
                    onChange={([value] = []) => {
                      if (!value) return;
                      captureEvent('cost-adjustment-override-added', {
                        overrideCategory,
                        adjustmentType,
                      });
                      push(value);
                      setAddingOverride(false);
                    }}
                    options={availableOptions}
                    placeholder={overrideCategory}
                    className="flex-grow-1"
                  />
                </div>
              </StyledRoundedGreyContainer>
            ) : (
              <StyledRoundedGreyContainer className="mt-2">
                <IconButton
                  color="link"
                  size="sm"
                  onClick={() => setAddingOverride(true)}
                >
                  <BlockIconSpan>＋</BlockIconSpan> Add override
                </IconButton>
              </StyledRoundedGreyContainer>
            ))}
        </>
      )}
    </FieldArray>
  );
};

const FormSection = ({
  adjustmentType, // Type of adjustment [buffer, margin, subsidy]
  title, // Title of the section
  name, // Name of the field in the form
  items, // Items the adjustment will apply to
  formatItem, // Format for an item or override
  overrideOptions, // Items for override options
  overrideCategory, // Word to describe override options
}) => {
  const { values } = useFormikContext();
  const overrides = values[`${name}Overrides`];

  return (
    <FormSectionContainer className="mt-2 ml-0">
      <SectionHeader>
        <h3>{title}</h3>
        {overrides?.length > 0 &&
          ['Base unit cost', 'Adjusted unit cost'].map((label) => (
            <CostHeading
              key={label}
              className="text-center font-tiny font-italic"
            >
              {label}
            </CostHeading>
          ))}
      </SectionHeader>
      <InlineValidatedInput
        label={`Category ${adjustmentType}`}
        type="number"
        step="any"
        min={0}
        name={name}
        placeholder="0"
        unit="%"
        labelClassName="flex-grow-1 d-block"
        maxWidth="70px"
        formGroupClassName="w-50"
        parse={asNumber}
      />
      {/* Count and list of items that the adjustment will apply to */}
      {items && (
        <ExpandableItemsList
          title={asQuantityWithUnit(
            title,
            title.slice(0, -1), // Assumes that the title is pluralized with an s on the end
          )(items.length)}
          items={items}
          formatItem={formatItem}
          onToggle={(newToggleState) => {
            // only care about recording an event when someone opens the list
            if (newToggleState) {
              captureEvent('cost-adjustment-operations-list-expanded', {
                adjustmentType,
              });
            }
          }}
        />
      )}
      {/* Overrides for specific items */}
      {overrideOptions && (
        <OverridesSection
          adjustmentType={adjustmentType}
          name={name}
          overrideOptions={overrideOptions}
          overrideCategory={overrideCategory}
          formatItem={formatItem}
        />
      )}
    </FormSectionContainer>
  );
};

const getInitialOverrideValues = (adjustmentType, data) =>
  data?.filter(
    ({ [`${adjustmentType}Percent`]: percent }) => percent != null,
  ) || [];

const transformOverrideValues = (adjustmentType, allItems, overrideItems) => {
  const percentKey = `${adjustmentType}Percent`;
  return allItems.reduce((updatedItems, item) => {
    const overrideValue =
      overrideItems.find(({ id }) => id === item.id)?.[percentKey] ?? null;
    // Update the values that have changed, including those that have been
    // removed.
    if (overrideValue !== item[percentKey]) {
      updatedItems.push({
        id: item.id,
        updatedAt: item.updatedAt,
        [percentKey]: overrideValue,
      });
    }
    return updatedItems;
  }, []);
};

const shouldShowMarginInBaseCost = (adjustmentType) =>
  !['margin', 'buffer'].includes(adjustmentType);

const shouldShowBufferInBaseCost = (adjustmentType) =>
  adjustmentType !== 'buffer';

const formatCropLink = (crop) => (
  <EditCropLink crop={crop}>{crop.name}</EditCropLink>
);

const formatOperationLink = (operation) => (
  <EditCropLink operation={operation} crop={operation.crop}>
    <b>{operation.crop.name}:</b> {operation.name}
  </EditCropLink>
);

const formatCapexLink = (capex) => (
  <EditCapexLink capex={capex}>{capex.name}</EditCapexLink>
);

const AdjustmentsForm = ({
  adjustmentType,
  pluralAdjustmentType = `${adjustmentType}s`,
  title = _.capitalize(pluralAdjustmentType),
}) => {
  const { farmId } = useParams();
  useSetBreadcrumbEffect([{ display: title }], [title]);
  const queryVariables = {
    applyBuffer: shouldShowBufferInBaseCost(adjustmentType),
    applyMargin: shouldShowMarginInBaseCost(adjustmentType),
  };
  const { data, msg } = useQueryWithFarmIdAndMsg(
    adjustmentsQuery(adjustmentType, pluralAdjustmentType),
    {
      variables: queryVariables,
      // Get the latest data in case there has been an update
      // to the capexes, operations, or crops
      fetchPolicy: 'cache-and-network',
    },
  );

  const initialValues = useMemo(() => {
    const farmCostAdjustment =
      data?.farm?.[`category${_.capitalize(pluralAdjustmentType)}`] || {};

    const geneticsPercentOverrides = getInitialOverrideValues(
      adjustmentType,
      data?.rawCrops,
    );

    const capexPercentOverrides = getInitialOverrideValues(
      adjustmentType,
      data?.rawCapexes,
    );
    return {
      ...farmCostAdjustment,
      geneticsPercentOverrides,
      capexPercentOverrides,
    };
  }, [adjustmentType, pluralAdjustmentType, data]);

  const [{ loading }, mutateAndSetState] = useMutateWithState();

  const UpsertAdjustmentMutation = getUpsertAdjustmentMutation(
    pluralAdjustmentType,
  );

  const [upsertMutation] = useMutation(UpsertAdjustmentMutation, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: adjustmentsQuery(adjustmentType, pluralAdjustmentType),
        variables: { farmId, ...queryVariables },
      },
    ],
  });

  const {
    description,
    percentValidator,
    getOperationsForCategory,
    getCapexesForOverride,
  } = ADJUSTMENT_TYPES[adjustmentType];

  const validationSchema = useMemo(() => {
    return createValidationSchema(percentValidator);
  }, [percentValidator]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        const castValues = validationSchema.cast(values);
        const transformedValues = {
          ...castValues,
          geneticsPercentOverrides: transformOverrideValues(
            adjustmentType,
            data.rawCrops,
            castValues.geneticsPercentOverrides,
          ),
          capexPercentOverrides: transformOverrideValues(
            adjustmentType,
            data.rawCapexes,
            castValues.capexPercentOverrides,
          ),
          __typename: undefined,
          farmId,
        };
        captureEvent('cost-adustments-saved', { adjustmentType });
        return mutateAndSetState(
          () => upsertMutation({ variables: { input: transformedValues } }),
          `${title} saved`,
        );
      }}
    >
      <Form className="my-3 overflow-hidden h-100">
        <UnsavedFormPrompt />
        <ShrinkContentColumn>
          <TopSection>
            <h1>{title}</h1>
            <p>{description}</p>
            <SubmitButton loading={loading} size="sm" className="mb-2">
              Save
            </SubmitButton>
          </TopSection>
          {msg ? (
            <div className="my-3">{msg}</div>
          ) : (
            <ShrinkContent className="overflow-auto">
              <FormSection
                adjustmentType={adjustmentType}
                title="Genetic Stock"
                name="geneticsPercent"
                overrideOptions={data?.rawCrops}
                overrideCategory="Crop"
                formatItem={formatCropLink}
              />
              {CATEGORIES.map((category) => {
                const sectionTitle = `${_.capitalize(category)} Operations`;
                return (
                  <FormSection
                    key={category}
                    adjustmentType={adjustmentType}
                    title={sectionTitle}
                    name={`${category}Percent`}
                    items={getOperationsForCategory(data?.rawCrops, category)}
                    formatItem={formatOperationLink}
                  />
                );
              })}
              <FormSection
                adjustmentType={adjustmentType}
                title="Capital Expenses"
                name="capexPercent"
                overrideOptions={getCapexesForOverride(data?.rawCapexes)}
                overrideCategory="Capex"
                formatItem={formatCapexLink}
              />
            </ShrinkContent>
          )}
        </ShrinkContentColumn>
      </Form>
    </Formik>
  );
};

export default AdjustmentsForm;
